/**
 * The list-and-detail shell the board's screens share.
 *
 * Tasks and Suggested are the same shape — a title, a toolbar, a pale card of
 * rows that scrolls on its own, and a detail pane beside it — so they are
 * built from the same pieces rather than two copies that drift apart.
 */

import { useEffect, type ReactNode, type RefObject } from 'react';
import { cn } from '@/lib/format.ts';

/** The pale card the rows sit on. */
export const LIST_CARD =
  'overflow-hidden bg-card sm:rounded-2xl sm:border sm:border-cardline sm:shadow-[0_1px_2px_rgba(20,24,42,0.10),0_14px_34px_rgba(20,24,42,0.16)]';

/** One row on that card — a task's or a suggestion's. */
export function listRowClass(selected: boolean): string {
  return cn(
    'relative flex cursor-pointer flex-col gap-1 border-b border-cardline px-3 py-2.5 transition-colors duration-100 outline-none',
    'sm:gap-[7px] sm:px-[18px] sm:py-[var(--row-pad)]',
    selected ? 'bg-cardsel shadow-[inset_3px_0_0_var(--color-cardbar)]' : 'hover:bg-cardhover',
    'focus-visible:shadow-[inset_3px_0_0_var(--color-accent)]',
  );
}

export function BoardLayout({
  title,
  blurb,
  above,
  toolbar,
  roomForFab,
  footer,
  children,
  detail,
  nothingSelected,
}: {
  title: string;
  blurb: ReactNode;
  /** Between the blurb and the toolbar. */
  above?: ReactNode;
  toolbar: ReactNode;
  /** Leave room below the list for the "+" button phones float over it. */
  roomForFab?: boolean;
  /** Under the list, outside its scroll — the composer. */
  footer?: ReactNode;
  /** The list itself. */
  children: ReactNode;
  /** The detail pane's content, or null when nothing is selected. */
  detail: ReactNode | null;
  nothingSelected: string;
}) {
  const selected = detail !== null;
  return (
    <>
      {/*
        Below `lg` there is only room for one pane, so the two swap rather than
        stack: the list until a row is picked, then the detail in its place,
        with the detail's own close button doubling as "back". At `lg` and up
        both show at once, same as always.
      */}
      <main className={cn('flex min-h-0 min-w-0 flex-1 flex-col', selected && 'hidden lg:flex')}>
        <header className="shrink-0 px-3 pt-2.5 sm:px-7 sm:pt-5.5">
          <h1 className="m-0 text-[18px] font-[650] tracking-[-0.01em] sm:text-[21px]">{title}</h1>
          {/*
            Worth a line on a wide screen with room to spare, but on a phone
            this is the line standing between the first row and the fold —
            and the screen's own name already carries most of what it says.
            Kept from `sm` up, sacrificed below it.
          */}
          <p className="hidden text-[13px] text-body sm:mt-1.5 sm:block sm:text-[13.5px]">{blurb}</p>

          {above}

          <div className="mt-2.5 flex items-center gap-1.5 overflow-x-auto border-b border-line pb-2 sm:mt-4 sm:pb-3 [&>*]:shrink-0">
            {toolbar}
          </div>
        </header>

        <div
          className={cn(
            // Edge to edge below `sm` — the list is the point of this screen on
            // a phone, so it gets the width a margin would otherwise spend.
            // The card's own background carries the pale tone all the way to
            // the glass; only from `sm` up is there room to frame it as an
            // object sitting on the page instead of being the page.
            'min-h-0 flex-1 overflow-y-auto pt-2 sm:px-7 sm:pt-3.5 sm:pb-6',
            roomForFab ? 'pb-20' : 'pb-4',
          )}
        >
          {children}
        </div>

        {footer}
      </main>

      <aside
        className={cn(
          'flex min-h-0 w-full flex-1 flex-col bg-aside lg:max-w-[760px] lg:border-l lg:border-line',
          !selected && 'hidden lg:flex',
        )}
      >
        {selected ? (
          detail
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center p-10 text-center">
            <div className="text-[14.5px] font-semibold text-t5">Nothing selected</div>
            <div className="mt-1.5 text-[13px] leading-relaxed text-mute">{nothingSelected}</div>
          </div>
        )}
      </aside>
    </>
  );
}

/** The search box at the head of a toolbar. `/` jumps here — see `useListKeys`. */
export function ListSearch({
  inputRef,
  value,
  onChange,
  placeholder,
  label,
  title,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  title?: string;
}) {
  return (
    <div className="relative w-[150px] sm:w-[200px]">
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            if (value) onChange('');
            else event.currentTarget.blur();
          }
        }}
        placeholder={placeholder}
        aria-label={label}
        title={title}
        className="rg-focus h-7 w-full rounded-lg border border-inputbd bg-input px-2.5 text-[16px] text-hi outline-none placeholder:text-faint sm:text-[12.5px]"
      />
    </div>
  );
}

/**
 * Arrow keys (and j/k) move through the list; Escape closes the pane; `/`
 * jumps to search. Reviewing eight rows should not mean eight round trips to
 * the mouse.
 */
export function useListKeys(
  ids: string[],
  selectedId: string | null,
  select: (id: string | null) => void,
  searchRef: RefObject<HTMLInputElement | null>,
) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) return;

      if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }

      if (event.key === 'Escape') {
        select(null);
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'j' && event.key !== 'k') {
        return;
      }
      if (ids.length === 0) return;
      event.preventDefault();

      const down = event.key === 'ArrowDown' || event.key === 'j';
      const at = selectedId ? ids.indexOf(selectedId) : -1;
      const next = at < 0 ? 0 : down ? Math.min(ids.length - 1, at + 1) : Math.max(0, at - 1);
      select(ids[next]!);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ids, selectedId, select, searchRef]);
}
